import { useState, useEffect } from "react";
import "../Styles/join.css";
import JoinModal from "../components/JoinModel";

const API_BASE_URL = "";

function Join() {
  const [isJoinOpen, setIsJoinOpen] = useState(false);

  // Pre-fills the Membership ID if the QR link has ?id=VHRD-EHC-0001
  const [memberId, setMemberId] = useState(
    new URLSearchParams(window.location.search).get("id") || ""
  );

  // Verify Membership ID: loading + result (shown as a result page)
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // null | { found, ... }

  const formatMemberSince = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const handleVerify = async () => {
    const id = memberId.trim();
    if (!id) {
      setVerifyResult({ found: false, message: "Please enter a Membership ID." });
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community-members/verify/${encodeURIComponent(id)}`
      );
      const data = await res.json().catch(() => ({}));
      setVerifyResult(
        res.ok
          ? data
          : { found: false, message: data.message || "No membership was found for this ID." }
      );
    } catch (err) {
      console.error(err);
      setVerifyResult({
        found: false,
        message: "Could not verify right now. Please try again.",
      });
    } finally {
      setVerifying(false);
    }
  };

  // When the page is opened with #verify (from the QR code),
  // scroll straight to the Verify Membership ID section.
  useEffect(() => {
    if (window.location.hash === "#verify") {
      const el = document.getElementById("verify");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, []);

  return (
    <>
      <section className="join-hero">
        {/* Single full-screen background image (no slideshow) */}
        <div className="join-hero-bg">
          <img
            src="/assets/join.png"
            alt="Vanakkam HRD community"
            className="join-hero-bg-img"
          />
        </div>

        {/* Dark left-to-right shade for text readability */}
        <div className="join-hero-overlay"></div>

        {/* Actual content, sits above the background */}
        <div className="join-hero-content">
          <div className="join-hero-left">
            <h1 className="join-hero-title">
              Join Vanakkam HRD – Where India's HR Community Connects, Learns, and Inspires.
            </h1>

            <button
              type="button"
              className="join-hero-btn"
              onClick={() => setIsJoinOpen(true)}
            >
              Become a Member
              <img
                src="/assets/About_arrow_icon.png"
                alt="Arrow Icon"
                className="join-arrow-icon"
              />
            </button>
          </div>
        </div>
      </section>

      {/* ===================================================
          WHY JOIN THE VANAKKAM HRD COMMUNITY SECTION
      =================================================== */}
      <section className="whyjoin-section">
        <div className="whyjoin-container">
          <h2 className="whyjoin-heading">Why Join the Vanakkam HRD Community?</h2>
          <div className="whyjoin-underline"></div>

          {/* Row 1 - 2 boxes */}
          <div className="whyjoin-row whyjoin-row-top">
            <div className="whyjoin-card">
              <div className="whyjoin-icon whyjoin-icon-blue">
                <img
                  src="/assets/join_icon_1.png"
                  alt="Join Icon 1"
                  className="whyjoin-icon-img"
                />
              </div>
              <h3 className="whyjoin-card-title">Networking Opportunities</h3>
              <p className="whyjoin-card-desc">
                Connect with HR professionals, business leaders, and industry experts. Build meaningful relationships that open doors to new opportunities, collaborations, and career growth.
              </p>
            </div>

            <div className="whyjoin-card">
              <div className="whyjoin-icon whyjoin-icon-blue">
                <img
                  src="/assets/join_icon_2.png"
                  alt="Join Icon 2"
                  className="whyjoin-icon-img"
                />
              </div>
              <h3 className="whyjoin-card-title">Fun, Friendship & Community</h3>
              <p className="whyjoin-card-desc">
                Learning is more meaningful when shared. Build lasting friendships, celebrate successes, and become part of a vibrant HR community that supports your professional journey.
              </p>
            </div>
          </div>

          {/* Row 2 - 3 boxes */}
          <div className="whyjoin-row whyjoin-row-bottom">
            <div className="whyjoin-card">
              <div className="whyjoin-icon whyjoin-icon-blue">
                <img
                  src="/assets/join_icon_3.png"
                  alt="Join Icon 3"
                  className="whyjoin-icon-img"
                />
              </div>
              <h3 className="whyjoin-card-title">Answers to Your Questions</h3>
              <p className="whyjoin-card-desc">
                Get practical guidance and expert advice on today's HR challenges, trends, and best practices from experienced professionals.
              </p>
            </div>

            <div className="whyjoin-card">
              <div className="whyjoin-icon whyjoin-icon-blue">
                <img
                  src="/assets/join_icon_4.png"
                  alt="Join Icon 4"
                  className="whyjoin-icon-img"
                />
              </div>
              <h3 className="whyjoin-card-title">Discussions & Insights</h3>
              <p className="whyjoin-card-desc">
                Engage in thought-provoking conversations, exchange ideas, and gain valuable insights on the future of HR, leadership, and workplace transformation.
              </p>
            </div>

            <div className="whyjoin-card">
              <div className="whyjoin-icon whyjoin-icon-blue">
                <img
                  src="/assets/join_icon_5.png"
                  alt="Join Icon 5"
                  className="whyjoin-icon-img"
                />
              </div>
              <h3 className="whyjoin-card-title">Solutions to Your Challenges</h3>
              <p className="whyjoin-card-desc">
                Discover innovative solutions, real-world case studies, and proven strategies to overcome workplace and people management challenges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================
          VERIFY MEMBERSHIP ID SECTION
          (QR code link: /jointhecommunity#verify)
      =================================================== */}
      <section className="verify-section" id="verify">
        <div className="verify-container">
          <div className="verify-left">
            <h2 className="verify-title">
              Verify a Vanakkam HRD Community Membership ID
            </h2>
            <p className="verify-desc">
              Verify the authenticity of a Vanakkam HRD community membership or
              issued certificate using a unique identification number. Give
              members a trusted credential they can share and employers a simple
              way to validate its authenticity.
            </p>
          </div>

          <div className="verify-card">
            <h3 className="verify-card-title">Verify Membership ID</h3>
            <p className="verify-card-sub">
              Enter the Membership ID to check its verification status.
            </p>

            <label htmlFor="verify-member-id" className="verify-label">
              Membership ID
            </label>
            <input
              id="verify-member-id"
              type="text"
              className="verify-input"
              placeholder="e.g. VHRD-EHC-0001"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            />

            <button
              type="button"
              className="verify-btn"
              onClick={handleVerify}
              disabled={verifying}
            >
              {verifying ? "Verifying..." : "Verify to Member ID"}
            </button>
          </div>
        </div>
      </section>

      {/* ===================================================
          VERIFY FEATURES (4 CARDS) SECTION
      =================================================== */}
      <section className="verifyfeat-section">
        <div className="verifyfeat-container">
          <div className="verifyfeat-card">
            <div className="verifyfeat-icon">
              <img
                src="/assets/jc_icn_01.png"
                alt="Unique Membership and Certificate ID"
                className="verifyfeat-icon-img"
              />
            </div>
            <h3 className="verifyfeat-title">Unique Membership & Certificate ID</h3>
            <p className="verifyfeat-desc">
              Every registered member and issued certificate can be assigned a
              unique reference ID for reliable identification.
            </p>
          </div>

          <div className="verifyfeat-card">
            <div className="verifyfeat-icon">
              <img
                src="/assets/jc_icn_02.png"
                alt="QR-Enabled Verification"
                className="verifyfeat-icon-img"
              />
            </div>
            <h3 className="verifyfeat-title">QR-Enabled Verification</h3>
            <p className="verifyfeat-desc">
              Scan the QR code on a digital membership card or certificate to
              access its verification page.
            </p>
          </div>

          <div className="verifyfeat-card">
            <div className="verifyfeat-icon">
              <img
                src="/assets/jc_icn_03.png"
                alt="Privacy-Conscious Records"
                className="verifyfeat-icon-img"
              />
            </div>
            <h3 className="verifyfeat-title">Privacy-Conscious Records</h3>
            <p className="verifyfeat-desc">
              Only approved verification information is displayed publicly,
              helping protect members' personal information.
            </p>
          </div>

          <div className="verifyfeat-card">
            <div className="verifyfeat-icon">
              <img
                src="/assets/jc_icn_04.png"
                alt="Trusted Verification Register"
                className="verifyfeat-icon-img"
              />
            </div>
            <h3 className="verifyfeat-title">Trusted Verification Register</h3>
            <p className="verifyfeat-desc">
              The production system will connect to an approved Vanakkam HRD
              membership and certificate register to verify records.
            </p>
          </div>
        </div>
      </section>

      {/* ===================================================
          VERIFY RESULT (opens after clicking "Verify to Member ID")
      =================================================== */}
      {verifyResult && (
        <div className="vr-overlay" onClick={() => setVerifyResult(null)}>
          <div className="vr-card" onClick={(e) => e.stopPropagation()}>
            <div className="vr-head">
              <h3>Membership Verification</h3>
              <p>Official Membership Status</p>
            </div>

            {verifyResult.found && verifyResult.verified ? (
              <div className="vr-body">
                <div className="vr-status vr-status--ok">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 12.5l2.7 2.7L16 9.5" />
                  </svg>
                  <h4>Membership Verified</h4>
                  <p>This membership ID is valid and registered with Vanakkam HRD.</p>
                </div>

                <div className="vr-rows">
                  <div className="vr-row"><span>Member Name</span><b>{verifyResult.fullName}</b></div>
                  <div className="vr-row"><span>Membership ID</span><b className="vr-id">{verifyResult.membershipId}</b></div>
                  <div className="vr-row"><span>Membership Type</span><b>{verifyResult.membershipType || "—"}</b></div>
                  <div className="vr-row"><span>Designation</span><b>{verifyResult.designation || "—"}</b></div>
                  <div className="vr-row"><span>Company</span><b>{verifyResult.company || "—"}</b></div>
                  <div className="vr-row"><span>Status</span><b className="vr-badge">Active</b></div>
                  <div className="vr-row"><span>Member Since</span><b>{formatMemberSince(verifyResult.memberSince)}</b></div>
                </div>

                <div className="vr-note">
                  <b>Authentic Membership Record</b>
                  <p>This record confirms the membership details available in the Vanakkam HRD verification system.</p>
                </div>
              </div>
            ) : verifyResult.found ? (
              <div className="vr-body">
                <div className="vr-status vr-status--pending">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                  <h4>Approval Pending</h4>
                  <p>This membership ID is registered, but the membership has not been approved yet.</p>
                </div>
                <div className="vr-rows">
                  <div className="vr-row"><span>Membership ID</span><b className="vr-id">{verifyResult.membershipId}</b></div>
                </div>
              </div>
            ) : (
              <div className="vr-body">
                <div className="vr-status vr-status--bad">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9 9l6 6M15 9l-6 6" />
                  </svg>
                  <h4>Membership Not Found</h4>
                  <p>{verifyResult.message}</p>
                </div>
              </div>
            )}

            <div className="vr-foot">
              <p>Verified through the official Vanakkam HRD membership portal.</p>
              <button type="button" className="vr-close" onClick={() => setVerifyResult(null)}>
                Verify Another ID
              </button>
            </div>
          </div>
        </div>
      )}

      <JoinModal
        isOpen={isJoinOpen}
        onClose={() => setIsJoinOpen(false)}
      />
    </>
  );
}

export default Join;